/**
 * `gwbkAutogenApi` — RTK Query slice for the "Autogenerate dashboards from a
 * gwbk" flow on the Dashboards page.
 *
 * Backed by the `axilon-api-service` BFF routes under
 * `/api/ontology/dashboards/gwbk-autogen/*` (see
 * `dashboard-autogen-routes.ts`). The job is tracked in-memory on the
 * api-service and screenshots are served from a temp dir there, so the
 * thumbnail + zip URLs are plain GETs against the api-service (the
 * `/api/ontology` surface is unauthenticated at the BFF) and don't need to
 * go through RTK.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { getOntologyServiceBaseUrl } from '../../config'
import { ontologyBaseQuery } from '../core'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()
const AUTOGEN_BASE = `${ONTOLOGY_SERVICE_URL}/dashboards/gwbk-autogen`

export interface GwbkSource {
  siteId: string
  gatewayId: string
  fileName: string
  submittedAt: string
}

export type GwbkAutogenState =
  | 'capturing'
  | 'planning'
  | 'building'
  | 'done'
  | 'error'
  | 'cancelled'

/** One dashboard the planner proposes, grouping related screenshots. */
export interface GwbkPlannedDashboard {
  title: string
  project: string
  purpose: string
  /** Capture names composing this dashboard; the first is the primary view. */
  shotNames: string[]
  notes?: string
}

/** A screenshot the planner judged a near-duplicate of another. */
export interface GwbkDuplicateShot {
  name: string
  /** The capture name to keep instead. */
  duplicateOf: string
  reason?: string
}

/** A screenshot the planner judged unusable (caught mid-load, blank, errored). */
export interface GwbkDiscardedShot {
  name: string
  reason?: string
}

/** The LLM plan produced after all screenshots are captured. */
export interface GwbkDashboardPlan {
  dashboards: GwbkPlannedDashboard[]
  duplicates: GwbkDuplicateShot[]
  /** Junk captures (still loading / blank / partial) dropped from the plan. */
  discarded?: GwbkDiscardedShot[]
  /** Capture names that exceeded the planner's image cap and were not reviewed. */
  unreviewed?: string[]
}

/** The outcome of one planned dashboard's builder agent. */
export interface GwbkDashboardBuild {
  title: string
  project: string
  /** Capture names that fed this build (primary first). */
  shotNames: string[]
  /** Whether the agent reported it finished what was asked. */
  completed: boolean
  /** Operator-facing summary from the agent's finalize call. */
  message: string
  /** Dashboard paths the agent created or edited. */
  dashboardPaths: string[]
  error?: string
}

export interface GwbkAutogenShot {
  name: string
  project: string
  routeKey: string
  url: string
  variant: string
  dynamic: boolean
  sampleParams?: Record<string, string>
  /** For dynamic routes: where sample ids came from. */
  sampleSource?: GwbkSampleSource
}

export interface GwbkAutogenLog {
  at: string
  level: 'info' | 'error'
  message: string
}

/** Lightweight job row for the history list (counts, not the full arrays). */
export interface GwbkAutogenJobSummary {
  id: string
  state: GwbkAutogenState
  siteId: string
  gatewayId: string
  gatewayUrl: string
  version: string
  total: number
  completed: number
  shotCount: number
  plannedCount: number
  builtCount: number
  buildFailedCount: number
  error?: string
  createdAt: string
  updatedAt: string
}

export interface GwbkAutogenJob {
  id: string
  state: GwbkAutogenState
  siteId: string
  gatewayId: string
  gatewayUrl: string
  version: string
  total: number
  completed: number
  shots: GwbkAutogenShot[]
  logs: GwbkAutogenLog[]
  /** The LLM dashboard plan, present once the planning step has run. */
  plan?: GwbkDashboardPlan
  /**
   * Per-planned-dashboard builder outcomes, positionally aligned with
   * `plan.dashboards` (`builds[i]` is the outcome for `dashboards[i]`). Builds
   * run concurrently and finish out of order, so a not-yet-finished slot is
   * `null` (sparse-array holes serialize to `null` over the wire).
   */
  builds?: (GwbkDashboardBuild | null)[]
  /**
   * Plan indices that have left the queue and are actively building. A planned
   * dashboard `i` is: done if `builds[i]` is set, building if `i` is here,
   * otherwise queued (waiting for a free builder slot).
   */
  buildingIndices?: number[]
  error?: string
  createdAt: string
  updatedAt: string
}

/**
 * How each loaded page is explored for extra screenshots:
 *  - `agent`: a vision LLM decides what to click (tabs, dropdowns, accordions, …). General, but costs LLM calls.
 *  - `tabs`: deterministic ARIA `role="tab"` cycling. Cheap, tabs only.
 *  - `none`: base capture only.
 */
export type GwbkInteractionMode = 'agent' | 'tabs' | 'none'

/** Where a dynamic route's suggested example path values came from. */
export type GwbkSampleSource = 'manual' | 'view-default' | 'graph' | 'placeholder'

/** One dynamic route surfaced in the dialog, with a pre-filled example path. */
export interface GwbkDynamicRoute {
  project: string
  /** The dynamic route template, e.g. `/equipment/:equipmentId`. */
  routeKey: string
  viewPath?: string
  title?: string
  /** Suggested concrete path the user can accept or edit, e.g. `/equipment/FLAT0001`. */
  suggestedPath: string
  suggestedSource: GwbkSampleSource
}

export interface GwbkAutogenPlan {
  gatewayUrl: string
  version: string
  /** Count of static routes captured as-is (no example needed). */
  staticCount: number
  dynamicRoutes: GwbkDynamicRoute[]
}

export interface BeginGwbkAutogenInput {
  siteId: string
  gatewayId: string
  maxDynamicSamples?: number
  interactionMode?: GwbkInteractionMode
  /** Max interact-then-screenshot steps per page when `interactionMode` is `agent`. */
  maxExploreSteps?: number
  /**
   * User-pasted concrete example path(s) per dynamic route key. The value may
   * hold multiple newline-separated paths. Routes left blank fall back to
   * automatic (graph/placeholder) sampling.
   */
  dynamicOverrides?: Record<string, string>
}

export interface BeginGwbkAutogenResult {
  jobId: string
  total: number
  gatewayUrl: string
}

/** Absolute URL of a captured screenshot (for `<img src>` thumbnails). */
export function gwbkAutogenShotUrl(jobId: string, name: string): string {
  return `${AUTOGEN_BASE}/jobs/${encodeURIComponent(jobId)}/shots/${encodeURIComponent(name)}`
}

/** Absolute URL of the all-screenshots zip download. */
export function gwbkAutogenDownloadUrl(jobId: string): string {
  return `${AUTOGEN_BASE}/jobs/${encodeURIComponent(jobId)}/download`
}

export const gwbkAutogenApi = createApi({
  reducerPath: 'gwbkAutogenApi',
  baseQuery: ontologyBaseQuery,
  refetchOnReconnect: true,
  tagTypes: ['GwbkAutogenJob'],
  endpoints: (builder) => ({
    listGwbkSources: builder.query<GwbkSource[], void>({
      query: () => `${AUTOGEN_BASE}/sources`,
      transformResponse: (response: { sources?: GwbkSource[] }) => response.sources ?? []
    }),
    getGwbkAutogenPlan: builder.query<GwbkAutogenPlan, { siteId: string; gatewayId: string }>({
      query: ({ siteId, gatewayId }) =>
        `${AUTOGEN_BASE}/plan?siteId=${encodeURIComponent(siteId)}&gatewayId=${encodeURIComponent(gatewayId)}`
    }),
    beginGwbkAutogen: builder.mutation<BeginGwbkAutogenResult, BeginGwbkAutogenInput>({
      query: (body) => ({
        url: `${AUTOGEN_BASE}/jobs`,
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'GwbkAutogenJob', id: 'LIST' }]
    }),
    listGwbkAutogenJobs: builder.query<GwbkAutogenJobSummary[], void>({
      query: () => `${AUTOGEN_BASE}/jobs`,
      transformResponse: (response: { jobs?: GwbkAutogenJobSummary[] }) => response.jobs ?? [],
      providesTags: (result) => [
        { type: 'GwbkAutogenJob', id: 'LIST' },
        ...(result ?? []).map((job) => ({ type: 'GwbkAutogenJob' as const, id: job.id }))
      ]
    }),
    getGwbkAutogenJob: builder.query<GwbkAutogenJob, string>({
      query: (jobId) => `${AUTOGEN_BASE}/jobs/${encodeURIComponent(jobId)}`,
      providesTags: (_result, _error, jobId) => [{ type: 'GwbkAutogenJob', id: jobId }]
    }),
    cancelGwbkAutogen: builder.mutation<GwbkAutogenJob, string>({
      query: (jobId) => ({
        url: `${AUTOGEN_BASE}/jobs/${encodeURIComponent(jobId)}/cancel`,
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, jobId) => [
        { type: 'GwbkAutogenJob', id: jobId },
        { type: 'GwbkAutogenJob', id: 'LIST' }
      ]
    }),
    deleteGwbkAutogen: builder.mutation<void, string>({
      query: (jobId) => ({
        url: `${AUTOGEN_BASE}/jobs/${encodeURIComponent(jobId)}`,
        method: 'DELETE'
      }),
      // Drop the row from the cached list immediately so the sidebar updates the
      // moment the user deletes — rather than waiting for the LIST invalidation
      // to round-trip (and racing the page's auto-select effect). Rolled back if
      // the request fails.
      async onQueryStarted(jobId, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          gwbkAutogenApi.util.updateQueryData('listGwbkAutogenJobs', undefined, (draft) => {
            const idx = draft.findIndex((job) => job.id === jobId)
            if (idx !== -1) draft.splice(idx, 1)
          })
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: (_result, _error, jobId) => [
        { type: 'GwbkAutogenJob', id: jobId },
        { type: 'GwbkAutogenJob', id: 'LIST' }
      ]
    })
  })
})

export const {
  useListGwbkSourcesQuery,
  useGetGwbkAutogenPlanQuery,
  useBeginGwbkAutogenMutation,
  useListGwbkAutogenJobsQuery,
  useGetGwbkAutogenJobQuery,
  useCancelGwbkAutogenMutation,
  useDeleteGwbkAutogenMutation
} = gwbkAutogenApi
